import { AnyDelta, AnyEntity } from "@iterate/common/types/domain";
import { db } from "./dexie";
import { Table } from "dexie";
import { z } from "zod/v4";
import { AnyEvent, UpdateEvent } from "@iterate/common/types/events";
import { LocalDb } from "./localdb";
import { syncEntityByType, type SyncEntityKind } from "./recordSchemas";

type ModelMap<K extends SyncEntityKind> = {
  table: () => Table<z.output<(typeof syncEntityByType)[K]["recordSchema"]>>;
  toolkit: (typeof syncEntityByType)[K];
};

async function pushEvent(event: AnyEvent) {
  console.log("event emitted", event);
  await LocalDb.merge(event);
  return await LocalDb.addEvent(event);
}

function getTools<K extends SyncEntityKind>(type: K): ModelMap<K> {
  switch (type) {
    case "metric_series":
      return {
        table: () => db.metricSeries,
        toolkit: syncEntityByType.metric_series,
      } as ModelMap<K>;
    case "goal":
      return {
        table: () => db.goals,
        toolkit: syncEntityByType.goal,
      } as ModelMap<K>;
    case "habit":
      return {
        table: () => db.habits,
        toolkit: syncEntityByType.habit,
      } as ModelMap<K>;
    case "instance":
      return {
        table: () => db.instances,
        toolkit: syncEntityByType.instance,
      } as ModelMap<K>;
    case "journal":
      return {
        table: () => db.journals,
        toolkit: syncEntityByType.journal,
      } as ModelMap<K>;
    case "metric":
      return {
        table: () => db.metrics,
        toolkit: syncEntityByType.metric,
      } as ModelMap<K>;
    case "task":
      return {
        table: () => db.tasks,
        toolkit: syncEntityByType.task,
      } as ModelMap<K>;
    default:
      throw new Error(`Unknown type: ${type}`);
  }
}

function tables(table: Table<any>) {
  return [table, db.eventLog] as const;
}

function update(rawDelta: AnyDelta): Promise<void> {
  const tools = getTools(rawDelta.type);
  const table = tools.table();
  return db.transaction("rw", tables(table), async () => {
    const record = await table.get(rawDelta.id);
    if (!record) {
      throw new Error(`Record not found for delta: ${rawDelta.id}`);
    }
    const entity = tools.toolkit.recordSchema.encode(record as never) as AnyEntity;
    const delta = { ...tools.toolkit.deltaSchema.parse(rawDelta) } as Record<string, unknown>;
    for (const key in delta) {
      if (
        delta[key] === (entity as Record<string, unknown>)[key] &&
        key !== "id" &&
        key !== "type"
      ) {
        delete delta[key];
      }
    }
    const event: UpdateEvent = {
      type: "update",
      delta: delta as AnyDelta,
    };
    console.log(`%cSync.update(${entity.type} ${entity.id})`, "color: blue", { delta });
    await pushEvent(event);
  });
}

function upsertRecord(rawEntity: AnyEntity, table: Table<unknown>) {
  const toolkit = syncEntityByType[rawEntity.type as SyncEntityKind];
  const entity = toolkit.entitySchema.parse(rawEntity) as unknown as AnyEntity;
  console.log("UPSERT ENTITY", rawEntity, entity);
  return db.transaction("rw", tables(table), async () => {
    const record = await table.get(entity.id);
    if (record) {
      const existing = toolkit.recordSchema.encode(record as never) as unknown as AnyEntity;
      const delta: Record<string, unknown> = {
        id: entity.id,
        type: entity.type,
      };
      for (const key in entity) {
        if (!Object.prototype.hasOwnProperty.call(entity, key)) {
          continue;
        }
        const k = key as keyof AnyEntity;
        if (entity[k] !== existing[k]) {
          delta[key] = entity[k];
        }
      }
      console.log(`Sync.update(${entity.type} ${entity.id})`, {
        delta,
        entity,
      });
      await pushEvent({
        type: "update",
        delta: delta as AnyDelta,
      });
    } else {
      console.log(`Sync.create(${entity.type} ${entity.id})`, { entity });
      await pushEvent({
        type: "create",
        entity,
      });
    }
  });
}

function upsert(entity: AnyEntity): Promise<void> {
  switch (entity.type) {
    case "metric_series":
      return upsertRecord(entity, db.metricSeries);
    case "goal":
      return upsertRecord(entity, db.goals);
    case "habit":
      return upsertRecord(entity, db.habits);
    case "instance":
      return upsertRecord(entity, db.instances);
    case "journal":
      return upsertRecord(entity, db.journals);
    case "metric":
      return upsertRecord(entity, db.metrics);
    case "task":
      return upsertRecord(entity, db.tasks);
  }
}

function del(entity: Pick<AnyEntity, "id" | "type">): Promise<void> {
  const tools = getTools(entity.type);
  if (!tools) {
    throw new Error(`Unknown entity type: ${entity.type}`);
  }
  const table = tools.table();
  return db.transaction("rw", tables(table), async () => {
    await pushEvent({
      type: "delete",
      entity: {
        id: entity.id,
        type: entity.type,
      },
    });
  });
}

export const Sync = {
  upsert,
  update,
  delete: del,
  tables,
};
